import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiUsers, FiMapPin, FiSearch, FiChevronRight } = FiIcons;

const Representatives = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-patriot-navy mb-4">Your Representatives</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Connect with your elected officials at every level of government. Stay informed and make your voice heard on issues that matter.
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Find Representatives Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white rounded-lg shadow-md overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-patriot-red bg-opacity-10 rounded-full flex items-center justify-center mr-4">
                  <SafeIcon icon={FiSearch} className="h-6 w-6 text-patriot-red" />
                </div>
                <h2 className="text-xl font-semibold text-patriot-navy">Find Your Representatives</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Enter your address to discover who represents you at the federal, state, and local levels of government.
              </p>
              <div className="space-y-4 mb-6">
                <div className="flex items-center text-sm text-gray-700">
                  <SafeIcon icon={FiChevronRight} className="h-4 w-4 text-patriot-red mr-2" />
                  Find your U.S. Senators and Representatives
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <SafeIcon icon={FiChevronRight} className="h-4 w-4 text-patriot-red mr-2" />
                  Discover your state and local officials
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <SafeIcon icon={FiChevronRight} className="h-4 w-4 text-patriot-red mr-2" />
                  Get contact information for all your elected officials
                </div>
              </div>
              <Link
                to="/representatives/lookup"
                className="block w-full bg-patriot-red text-white text-center px-4 py-3 rounded-lg hover:bg-patriot-red-dark transition-colors duration-200 font-medium"
              >
                Look Up My Representatives
              </Link>
            </div>
          </motion.div>

          {/* Contact Officials Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-lg shadow-md overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-patriot-navy bg-opacity-10 rounded-full flex items-center justify-center mr-4">
                  <SafeIcon icon={FiUsers} className="h-6 w-6 text-patriot-navy" />
                </div>
                <h2 className="text-xl font-semibold text-patriot-navy">Why Contact Your Officials?</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Your representatives work for you. Reaching out to them is an essential part of our democratic process and helps ensure your voice is heard.
              </p>
              <div className="space-y-4 mb-6">
                <div className="flex items-start text-sm text-gray-700">
                  <SafeIcon icon={FiChevronRight} className="h-4 w-4 text-patriot-navy mt-1 mr-2" />
                  <div>
                    <strong className="font-medium">Influence Policy:</strong> Share your views on legislation and issues that matter to you
                  </div>
                </div>
                <div className="flex items-start text-sm text-gray-700">
                  <SafeIcon icon={FiChevronRight} className="h-4 w-4 text-patriot-navy mt-1 mr-2" />
                  <div>
                    <strong className="font-medium">Get Help:</strong> Representatives can assist with federal agencies and programs
                  </div>
                </div>
                <div className="flex items-start text-sm text-gray-700">
                  <SafeIcon icon={FiChevronRight} className="h-4 w-4 text-patriot-navy mt-1 mr-2" />
                  <div>
                    <strong className="font-medium">Stay Informed:</strong> Learn about upcoming legislation and events in your area
                  </div>
                </div>
              </div>
              <Link
                to="/resources"
                className="block w-full bg-patriot-navy text-white text-center px-4 py-3 rounded-lg hover:bg-opacity-90 transition-colors duration-200 font-medium"
              >
                View Resources
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Find Your District */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-semibold text-patriot-navy mb-6">Find Your Congressional District</h2>
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-1/2 mb-6 md:mb-0 md:pr-8">
              <p className="text-gray-700 mb-4">
                Your congressional district determines which U.S. House Representative serves you in Congress. Districts are redrawn every 10 years based on census data.
              </p>
              <div className="flex items-center text-sm text-gray-600 mb-4">
                <SafeIcon icon={FiMapPin} className="h-4 w-4 mr-2 text-patriot-red" />
                <span>Each state has between 1 and 52 congressional districts</span>
              </div>
              <Link
                to="/representatives/lookup"
                className="inline-flex items-center text-patriot-red hover:text-patriot-red-dark font-medium"
              >
                Find your district <SafeIcon icon={FiChevronRight} className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <div className="md:w-1/2">
              <img
                src="https://www.census.gov/mycd/application/assets/img/map.png"
                alt="Congressional District Map"
                className="rounded-lg shadow-sm w-full"
              />
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-semibold text-patriot-navy mb-6">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-patriot-navy mb-2">How do I contact my representative?</h3>
              <p className="text-sm text-gray-600">
                The most effective ways to contact your representatives are through official emails, phone calls to their offices, or attending their local town halls and events.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-patriot-navy mb-2">What information does my representative need from me?</h3>
              <p className="text-sm text-gray-600">
                When contacting your representative, include your full name, address (to verify you're a constituent), and a clear, specific message about the issue you're addressing.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-patriot-navy mb-2">How often are representatives elected?</h3>
              <p className="text-sm text-gray-600">
                U.S. Representatives serve two-year terms, Senators serve six-year terms, and state and local officials' terms vary by position and location.
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-patriot-navy mb-2">What services can my representative's office provide?</h3>
              <p className="text-sm text-gray-600">
                Representatives' offices can help with federal agency issues, including Social Security, Medicare, immigration matters, veterans' benefits, and passport problems.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 bg-patriot-red rounded-lg p-8 text-white text-center"
        >
          <h2 className="text-2xl font-bold mb-4">Ready to Find Your Representatives?</h2>
          <p className="text-gray-100 mb-6 max-w-2xl mx-auto">
            Enter your address to discover who represents you at every level of government, from your local officials to your U.S. Senators.
          </p>
          <Link
            to="/representatives/lookup"
            className="inline-block bg-white text-patriot-red px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium"
          >
            Find My Representatives Now
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Representatives;